import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
    const [cart, setCart] = useState({ items: [] });
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('stripe');
    const navigate = useNavigate();

    useEffect(() => {
        fetchCart();
    }, []);

    const fetchCart = async () => {
        try {
            const response = await axios.get(`${API}/cart`);
            if (response.data.items.length === 0) {
                navigate('/cart');
                return;
            }
            setCart(response.data);
        } catch (error) {
            console.error('Error fetching cart:', error);
            toast.error('Failed to load cart');
        } finally {
            setLoading(false);
        }
    };

    const calculateTotal = () => {
        return cart.items.reduce((total, item) => {
            return total + (item.product.price * item.quantity);
        }, 0).toFixed(2);
    };

    const handleStripeCheckout = async () => {
        setProcessing(true);
        try {
            const originUrl = window.location.origin;
            const response = await axios.post(`${API}/checkout/session`, {
                origin_url: originUrl,
                payment_method: 'stripe',
                metadata: {
                    source: 'web_checkout'
                }
            });

            // Redirect to Stripe Checkout
            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error('Checkout error:', error);
            toast.error(error.response?.data?.detail || 'Checkout failed');
            setProcessing(false);
        }
    };

    const handlePayPalCheckout = async () => {
        setProcessing(true);
        try {
            const originUrl = window.location.origin;
            const response = await axios.post(`${API}/checkout/paypal/create-order`, {
                origin_url: originUrl,
                payment_method: 'paypal',
                metadata: {
                    source: 'web_checkout'
                }
            });

            // Redirect to PayPal approval URL
            if (response.data.approval_url) {
                window.location.href = response.data.approval_url;
            }
        } catch (error) {
            console.error('PayPal checkout error:', error);
            toast.error(error.response?.data?.detail || 'PayPal checkout failed');
            setProcessing(false);
        }
    };

    const handleCheckout = () => {
        if (paymentMethod === 'stripe') {
            handleStripeCheckout();
        } else if (paymentMethod === 'paypal') {
            handlePayPalCheckout();
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-16">
                <div className="text-xl text-muted-foreground">Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-24 px-6 md:px-12 lg:px-24 py-12" data-testid="checkout-page">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-8">
                    <Lock className="w-6 h-6 text-primary" />
                    <h1 className="text-4xl md:text-5xl font-bold tracking-tighter">Secure Checkout</h1>
                </div>

                <div className="grid lg:grid-cols-2 gap-12">
                    {/* Order Summary */}
                    <div data-testid="order-summary">
                        <h2 className="text-2xl font-semibold mb-6">Order Summary</h2>
                        <div className="space-y-4 mb-6">
                            {cart.items.map((item) => (
                                <div 
                                    key={item.product.id}
                                    className="flex gap-4"
                                    data-testid={`checkout-item-${item.product.id}`}
                                >
                                    <img 
                                        src={item.product.image_url} 
                                        alt={item.product.name}
                                        className="w-16 h-16 object-cover"
                                    />
                                    <div className="flex-1">
                                        <h3 className="font-medium">{item.product.name}</h3>
                                        <p className="text-sm text-muted-foreground">${item.product.price}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="border-t border-border pt-4">
                            <div className="flex justify-between text-xl font-bold mb-2">
                                <span>Total</span>
                                <span className="text-primary" data-testid="checkout-total">${calculateTotal()}</span>
                            </div>
                            <p className="text-sm text-muted-foreground">All transactions are secure and encrypted</p>
                        </div>
                    </div>

                    {/* Payment Section */}
                    <div data-testid="payment-section">
                        <h2 className="text-2xl font-semibold mb-6">Payment Method</h2>
                        
                        <div className="space-y-4 mb-6">
                            <button
                                onClick={() => setPaymentMethod('stripe')}
                                className={`w-full bg-card border p-6 text-left transition-colors ${paymentMethod === 'stripe' ? 'border-primary' : 'border-border hover:border-white/20'}`}
                                data-testid="select-stripe"
                            >
                                <div className="flex items-center gap-3 mb-2">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'stripe' ? 'border-primary' : 'border-muted-foreground'}`}>
                                        {paymentMethod === 'stripe' && <div className="w-3 h-3 rounded-full bg-primary"></div>}
                                    </div>
                                    <CreditCard className="w-6 h-6 text-primary" />
                                    <h3 className="font-semibold">Stripe</h3>
                                </div>
                                <p className="text-sm text-muted-foreground ml-8">
                                    Credit cards, debit cards, and more payment methods
                                </p>
                            </button>

                            <button
                                onClick={() => setPaymentMethod('paypal')}
                                className={`w-full bg-card border p-6 text-left transition-colors ${paymentMethod === 'paypal' ? 'border-primary' : 'border-border hover:border-white/20'}`}
                                data-testid="select-paypal"
                            >
                                <div className="flex items-center gap-3 mb-2">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'paypal' ? 'border-primary' : 'border-muted-foreground'}`}>
                                        {paymentMethod === 'paypal' && <div className="w-3 h-3 rounded-full bg-primary"></div>}
                                    </div>
                                    <div className="bg-[#0070BA] text-white px-3 py-1 rounded font-bold text-sm">PayPal</div>
                                </div>
                                <p className="text-sm text-muted-foreground ml-8">
                                    Pay securely with your PayPal account
                                </p>
                            </button>
                        </div>
                        
                        <button 
                            onClick={handleCheckout}
                            disabled={processing}
                            className="btn-primary w-full"
                            data-testid="proceed-to-payment-button"
                        >
                            {processing ? 'Processing...' : `Pay with ${paymentMethod === 'stripe' ? 'Stripe' : 'PayPal'}`}
                        </button>

                        <div className="bg-muted/30 border border-border p-4 mt-6">
                            <p className="text-xs text-muted-foreground">
                                By completing your purchase, you agree to our terms of service. All digital products are delivered instantly after payment confirmation.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;
